import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAuth0 } from '@auth0/auth0-react';
import { storage } from '../../services/storage';
import './DeleteDataDrawer.css';

interface DeleteDataDrawerProps {
  onClose: () => void;
}

export const DeleteDataDrawer: React.FC<DeleteDataDrawerProps> = ({ onClose }) => {
  const { logout: coachLogout } = useAuth();
  const { logout: auth0Logout } = useAuth0();
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isConfirmValid = confirmText.toLowerCase() === 'delete';

  const handleDeleteData = async () => {
    if (!isConfirmValid) return;

    setIsDeleting(true);
    setError(null);
    
    try {
      // Get JWT token for authenticated request
      const jwtToken = await storage.getItem('jwt_token') || await storage.getItem('token');
      if (!jwtToken) {
        throw new Error('No authentication token found');
      }

      const response = await fetch('/api/users/delete-data', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        },
        body: JSON.stringify({ confirmation: confirmText })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ DATA DELETION SUCCESS:', result);
        
        // Show success message
        setShowConfirm(true);
        
        // Wait 2 seconds, then clear all local data and logout
        setTimeout(async () => {
          try {
            // Clear all local storage
            await storage.clear();
            
            // Clear Coach Cedar session
            coachLogout();
            
            // Logout from Auth0 (user deleted from Auth0 in background)
            auth0Logout({
              logoutParams: {
                returnTo: window.location.origin
              }
            });
          } catch (e) {
            console.warn('⚠️ Cleanup error during logout:', e);
            // Force redirect even if logout fails
            window.location.href = '/';
          }
        }, 2000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete data');
      }
    } catch (error: any) {
      console.error('❌ Failed to delete data:', error);
      setError(error.message || 'Failed to delete data. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setConfirmText('');
    setError(null);
    onClose();
  };

  if (showConfirm) {
    return (
      <div className="delete-drawer-overlay" onClick={onClose}>
        <div className="delete-drawer" onClick={(e) => e.stopPropagation()}>
          <div className="drawer-handle"></div>
          <div className="delete-success">
            <div className="success-icon">✓</div>
            <h2>Account Deleted</h2>
            <p>Your data has been permanently deleted. Redirecting...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="delete-drawer-overlay" onClick={onClose}>
      <div className="delete-drawer" onClick={(e) => e.stopPropagation()}>
        {/* Drawer handle */}
        <div className="drawer-handle"></div>
        
        <h2 className="drawer-title delete-title">Delete My Data</h2>

        <div className="delete-warning">
          <div className="warning-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 9v4"></path>
              <path d="M12 17h.01"></path>
              <circle cx="12" cy="12" r="10"></circle>
            </svg>
          </div>
          <p className="warning-text">
            This permanently deletes your entire Coach Cedar account including all workouts, goals, plans, chat history, health data, device connections, and profile information. This action cannot be undone.
          </p>
        </div>

        {error && (
          <div className="delete-error">
            <div className="error-icon">⚠️</div>
            <p className="error-text">{error}</p>
          </div>
        )}

        <div className="delete-form">
          <div className="confirm-section">
            <label className="confirm-label">
              Type DELETE to confirm permanent deletion
            </label>
            <input
              type="text"
              className="confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE here"
              autoCapitalize="none"
              autoComplete="off"
            />
          </div>

          <div className="delete-actions">
            <button
              className={`delete-button ${isConfirmValid ? 'enabled' : 'disabled'}`}
              onClick={handleDeleteData}
              disabled={!isConfirmValid || isDeleting}
            >
              {isDeleting ? (
                <>
                  <div className="button-spinner"></div>
                  Deleting...
                </>
              ) : (
                'Delete My Account'
              )}
            </button>
            
            <button 
              className="cancel-button"
              onClick={handleCancel}
              disabled={isDeleting}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 